/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect,useCallback} from 'react';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import SlidePanel from '../components/common/SlidePanel';
import {Select} from '../components/common/FormField';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import api from '../services/api';
import {fmtDate,fmtDateTime,fmtCurrency,safeArray} from '../utils/helpers';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';

const PAYER_TYPES=['INSURANCE','TPA','CORPORATE','GOVERNMENT_SCHEME','CASH'];
const PAYER_TYPE_META:Record<string,{icon:string;color:string}>={
  INSURANCE:{icon:'🏥',color:'bg-blue-100 text-[#02357A]'},
  TPA:{icon:'🤝',color:'bg-purple-100 text-purple-700'},
  CORPORATE:{icon:'🏢',color:'bg-indigo-100 text-indigo-700'},
  GOVERNMENT_SCHEME:{icon:'🏛️',color:'bg-green-100 text-green-700'},
  CASH:{icon:'💵',color:'bg-slate-100 text-slate-600'},
};
const SERVICE_CATEGORIES=['CONSULTATION','LAB','RADIOLOGY','ROOM_RENT','PROCEDURE','PHARMACY','OTHER'];
const AUTH_STATUS_META:Record<string,string>={
  PENDING:'bg-yellow-100 text-yellow-700',
  APPROVED:'bg-green-100 text-green-700',
  PARTIALLY_APPROVED:'bg-amber-100 text-amber-700',
  REJECTED:'bg-red-100 text-red-700',
  QUERY_RAISED:'bg-purple-100 text-purple-700',
  EXPIRED:'bg-gray-100 text-gray-500',
};

export default function PayerManagementPage(){
  const {t}=useTranslation();
  const [tab,setTab]=useState<'payers'|'preauth'>('payers');
  const [payers,setPayers]=useState<any[]>([]);
  const [stats,setStats]=useState<any>({});
  const [preauths,setPreauths]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [filterType,setFilterType]=useState('');
  const [filterAuthStatus,setFilterAuthStatus]=useState('');
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');

  const [showPayerForm,setShowPayerForm]=useState(false);
  const [editingPayer,setEditingPayer]=useState<any>(null);
  const [showTariffs,setShowTariffs]=useState<any>(null);
  const [tariffs,setTariffs]=useState<any[]>([]);
  const [showAuthForm,setShowAuthForm]=useState(false);
  const [showAuthDetail,setShowAuthDetail]=useState<any>(null);

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const PAYER_EMPTY={
    name:'',payerType:'INSURANCE',code:'',contactPerson:'',phone:'',email:'',address:'',
    copayPercentage:0,creditLimit:'',contractStart:'',contractEnd:'',notes:'',
  };
  const [payerForm,setPayerForm]=useState<any>(PAYER_EMPTY);

  const TARIFF_EMPTY={serviceCategory:'CONSULTATION',serviceName:'',serviceCode:'',standardPrice:'',negotiatedPrice:''};
  const [tariffForm,setTariffForm]=useState<any>(TARIFF_EMPTY);

  const [selectedPatient,setSelectedPatient]=useState<any>(null);
  const AUTH_EMPTY={
    payerId:'',policyNumber:'',policyHolderName:'',relationshipToPatient:'Self',
    diagnosis:'',treatmentPlan:'',requestedAmount:'',expiryDate:'',admissionId:'',
  };
  const [authForm,setAuthForm]=useState<any>(AUTH_EMPTY);
  const [authUpdate,setAuthUpdate]=useState({status:'APPROVED',sanctionedAmount:'',tpaReferenceNumber:'',remarks:''});

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const [p,s]=await Promise.all([
        api.getPayers({payerType:filterType||undefined}),
        api.getPayerStats(),
      ]);
      setPayers(Array.isArray(p.data)?p.data:safeArray(p.data));
      setStats(s.data||{});
    }catch{}finally{setLoading(false);}
  },[filterType]);

  const loadPreauths=useCallback(async()=>{
    setLoading(true);
    try{
      const r=await api.getPreAuths({status:filterAuthStatus||undefined});
      setPreauths(Array.isArray(r.data)?r.data:safeArray(r.data));
    }catch{}finally{setLoading(false);}
  },[filterAuthStatus]);

  useEffect(()=>{tab==='payers'?load():loadPreauths();},[tab,filterType,filterAuthStatus]);

  /* ── Payer CRUD ───────────────────────────────────────────────────────── */
  const openPayerForm=(p?:any)=>{
    if(p){
      setEditingPayer(p);
      setPayerForm({
        name:p.name,payerType:p.payerType,code:p.code||'',contactPerson:p.contactPerson||'',
        phone:p.phone||'',email:p.email||'',address:p.address||'',
        copayPercentage:p.copayPercentage||0,creditLimit:p.creditLimit||'',
        contractStart:p.contractStart?String(p.contractStart).slice(0,10):'',
        contractEnd:p.contractEnd?String(p.contractEnd).slice(0,10):'',
        notes:p.notes||'',
      });
    }else{
      setEditingPayer(null);
      setPayerForm(PAYER_EMPTY);
    }
    setShowPayerForm(true);
  };

  const savePayer=async()=>{
    if(!payerForm.name){notify('⚠️ Enter payer name');return;}
    setSaving(true);
    try{
      if(editingPayer) await api.updatePayer(editingPayer.id,payerForm);
      else await api.createPayer(payerForm);
      notify('✅ Payer saved!');
      setShowPayerForm(false);load();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const toggleActive=async(p:any)=>{
    try{await api.updatePayer(p.id,{isActive:!p.isActive});notify('✅ Updated');load();}
    catch{notify('❌ Error');}
  };

  /* ── Tariffs ──────────────────────────────────────────────────────────── */
  const openTariffs=async(p:any)=>{
    setShowTariffs(p);
    try{
      const r=await api.getPayerTariffs(p.id);
      setTariffs(Array.isArray(r.data)?r.data:safeArray(r.data));
    }catch{setTariffs([]);}
  };

  const addTariff=async()=>{
    if(!tariffForm.serviceName||!tariffForm.negotiatedPrice){notify('⚠️ Service name and negotiated price required');return;}
    setSaving(true);
    try{
      await api.addPayerTariff(showTariffs.id,tariffForm);
      const r=await api.getPayerTariffs(showTariffs.id);
      setTariffs(Array.isArray(r.data)?r.data:safeArray(r.data));
      setTariffForm(TARIFF_EMPTY);
      notify('✅ Tariff added');
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const removeTariff=async(id:string)=>{
    try{
      await api.removePayerTariff(id);
      setTariffs(tariffs.filter(t=>t.id!==id));
      notify('✅ Tariff removed');
    }catch{notify('❌ Error');}
  };

  /* ── Pre-Auth ─────────────────────────────────────────────────────────── */
  const saveAuth=async()=>{
    if(!selectedPatient||!authForm.payerId||!authForm.diagnosis){notify('⚠️ Patient, payer and diagnosis are required');return;}
    setSaving(true);
    try{
      await api.createPreAuth({...authForm,patientId:selectedPatient.id});
      notify('✅ Pre-authorization request created!');
      setShowAuthForm(false);
      setAuthForm(AUTH_EMPTY);setSelectedPatient(null);
      loadPreauths();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const updateAuthStatus=async()=>{
    if(!showAuthDetail)return;
    setSaving(true);
    try{
      await api.updatePreAuth(showAuthDetail.id,authUpdate);
      notify('✅ Authorization updated');
      setShowAuthDetail(null);
      loadPreauths();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  return(
    <Layout title="Insurance & TPA Management" actions={
      tab==='payers'
        ?<button onClick={()=>openPayerForm()} className="btn-primary text-sm">+ New Payer</button>
        :<button onClick={()=>setShowAuthForm(true)} className="btn-primary text-sm">+ New Pre-Authorization</button>
    }>
      {msg&&(
        <div className={`mb-4 p-3 rounded-lg text-sm border ${msg.startsWith('✅')?'bg-green-50 text-green-700 border-green-200':'bg-red-50 text-red-700 border-red-200'}`}>{msg}</div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard label="Total Payers"    value={stats.total||0}        icon="🤝" color="blue"/>
        <StatCard label="Active Payers"   value={stats.active||0}       icon="✅" color="green"/>
        <StatCard label="Pending Pre-Auths" value={stats.pendingAuths||0} icon="⏳" color="amber"/>
        <StatCard label="Payer Types"     value={(stats.byType||[]).length} icon="📊" color="purple"/>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[['payers','🤝 Payers & Tariffs'],['preauth','📋 Pre-Authorizations']].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab===t?'bg-[#02357A] text-white shadow':'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* ── PAYERS TAB ── */}
      {tab==='payers'&&(
        <div>
          <div className="flex gap-2 mb-4 flex-wrap">
            <button onClick={()=>setFilterType('')}
              className={`text-xs px-3 py-1.5 rounded-full border ${!filterType?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-200 text-slate-600'}`}>{t('common.all','All')}</button>
            {PAYER_TYPES.map(t=>(
              <button key={t} onClick={()=>setFilterType(t===filterType?'':t)}
                className={`text-xs px-3 py-1.5 rounded-full border ${filterType===t?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-200 text-slate-600'}`}>
                {PAYER_TYPE_META[t]?.icon} {t.replace('_',' ')}
              </button>
            ))}
          </div>

          {loading?(
            <div className="card p-12 text-center"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
          ):payers.length===0?(
            <div className="card p-16 text-center"><span className="text-5xl block mb-3">🤝</span><p className="text-slate-400">No payers configured</p></div>
          ):(
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {payers.map((p:any,i:number)=>{
                const meta=PAYER_TYPE_META[p.payerType]||PAYER_TYPE_META.CASH;
                return(
                  <div key={i} className={`card p-4 ${!p.isActive?'opacity-50':''}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold text-slate-800">{p.name}</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>{meta.icon} {p.payerType.replace('_',' ')}</span>
                      </div>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={p.isActive} onChange={()=>toggleActive(p)} className="w-4 h-4"/>
                        <span className="text-xs text-slate-400">{t('common.active','Active')}</span>
                      </label>
                    </div>
                    {p.code&&<p className="text-xs text-slate-400 font-mono">{p.code}</p>}
                    {p.contactPerson&&<p className="text-sm text-slate-600 mt-1">👤 {p.contactPerson}</p>}
                    {p.phone&&<p className="text-xs text-slate-400">📞 {p.phone}</p>}
                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-slate-400">Co-Pay</p>
                        <p className="font-bold text-slate-700">{p.copayPercentage}%</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-slate-400">Credit Limit</p>
                        <p className="font-bold text-slate-700">{p.creditLimit?fmtCurrency(p.creditLimit):'—'}</p>
                      </div>
                    </div>
                    {(p.contractEnd)&&(
                      <p className="text-xs text-slate-400 mt-2">
                        Contract valid till: {fmtDate(p.contractEnd)}
                        {new Date(p.contractEnd)<new Date()&&<span className="text-red-500 font-bold ml-1">⚠️ Expired</span>}
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button onClick={()=>openPayerForm(p)} className="btn-secondary text-xs flex-1 py-1.5">✏️ Edit</button>
                      <button onClick={()=>openTariffs(p)} className="btn-secondary text-xs flex-1 py-1.5">💰 Tariffs</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── PRE-AUTH TAB ── */}
      {tab==='preauth'&&(
        <div>
          <div className="flex gap-2 mb-4 flex-wrap">
            <button onClick={()=>setFilterAuthStatus('')}
              className={`text-xs px-3 py-1.5 rounded-full border ${!filterAuthStatus?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-200 text-slate-600'}`}>{t('common.all','All')}</button>
            {Object.keys(AUTH_STATUS_META).map(s=>(
              <button key={s} onClick={()=>setFilterAuthStatus(s===filterAuthStatus?'':s)}
                className={`text-xs px-3 py-1.5 rounded-full border ${filterAuthStatus===s?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-200 text-slate-600'}`}>
                {s.replace('_',' ')}
              </button>
            ))}
          </div>

          {loading?(
            <div className="card p-12 text-center"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
          ):preauths.length===0?(
            <div className="card p-16 text-center"><span className="text-5xl block mb-3">📋</span><p className="text-slate-400">No pre-authorization requests</p></div>
          ):(
            <div className="space-y-3">
              {preauths.map((a:any,i:number)=>{
                const payer=payers.find(p=>p.id===(a.payerId||a.payer_id));
                return(
                  <div key={i} className="card p-4">
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-mono font-black text-[#02357A] text-sm">{a.authNumber||a.auth_number}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${AUTH_STATUS_META[a.status]||'bg-slate-100'}`}>{a.status.replace('_',' ')}</span>
                          {payer&&<span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{payer.name}</span>}
                        </div>
                        <p className="font-semibold text-slate-700 text-sm">Patient: {a.patientId||a.patient_id}</p>
                        <p className="text-sm text-slate-600 mt-1">{a.diagnosis}</p>
                        {(a.policyNumber||a.policy_number)&&<p className="text-xs text-slate-400 mt-0.5">Policy: {a.policyNumber||a.policy_number} · {a.policyHolderName||a.policy_holder_name}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-slate-400">Requested</p>
                        <p className="font-bold text-slate-700">{fmtCurrency(a.requestedAmount||a.requested_amount)}</p>
                        {(a.sanctionedAmount||a.sanctioned_amount)&&(
                          <>
                            <p className="text-xs text-slate-400 mt-1">Sanctioned</p>
                            <p className="font-bold text-green-600">{fmtCurrency(a.sanctionedAmount||a.sanctioned_amount)}</p>
                          </>
                        )}
                        <button onClick={()=>{setShowAuthDetail(a);setAuthUpdate({status:'APPROVED',sanctionedAmount:String(a.sanctionedAmount||a.requested_amount||a.requestedAmount||''),tpaReferenceNumber:a.tpaReferenceNumber||'',remarks:''});}}
                          className="btn-secondary text-xs py-1 px-3 mt-2">Update Status</button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">{fmtDateTime(a.createdAt||a.created_at)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ PAYER FORM MODAL ═══ */}
      <SlidePanel isOpen={showPayerForm} onClose={()=>setShowPayerForm(false)} title={editingPayer?'✏️ Edit Payer':'+ New Payer'} size="lg">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Payer Name *</label>
              <input className="input" value={payerForm.name} onChange={e=>setPayerForm({...payerForm,name:e.target.value})} placeholder="e.g. Star Health Insurance"/>
            </div>
            <div>
              <label className="label">Payer Type</label>
              <Select value={payerForm.payerType} onChange={(e:any)=>setPayerForm({...payerForm,payerType:e.target.value})}>
                {PAYER_TYPES.map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
              </Select>
            </div>
            <div>
              <label className="label">{t('common.code','Code')}</label>
              <input className="input" value={payerForm.code} onChange={e=>setPayerForm({...payerForm,code:e.target.value})} placeholder="e.g. STAR"/>
            </div>
            <div>
              <label className="label">Contact Person</label>
              <input className="input" value={payerForm.contactPerson} onChange={e=>setPayerForm({...payerForm,contactPerson:e.target.value})}/>
            </div>
            <div>
              <label className="label">{t('common.phone','Phone')}</label>
              <input className="input" value={payerForm.phone} onChange={e=>setPayerForm({...payerForm,phone:e.target.value})}/>
            </div>
            <div>
              <label className="label">{t('common.email','Email')}</label>
              <input className="input" value={payerForm.email} onChange={e=>setPayerForm({...payerForm,email:e.target.value})}/>
            </div>
            <div>
              <label className="label">Co-Pay Percentage (%)</label>
              <input type="number" className="input" value={payerForm.copayPercentage} onChange={e=>setPayerForm({...payerForm,copayPercentage:e.target.value})}/>
            </div>
            <div>
              <label className="label">Credit Limit ({getCurrencySymbol()})</label>
              <input type="number" className="input" value={payerForm.creditLimit} onChange={e=>setPayerForm({...payerForm,creditLimit:e.target.value})}/>
            </div>
            <div>
              <label className="label">Contract Start</label>
              <input type="date" className="input" value={payerForm.contractStart} onChange={e=>setPayerForm({...payerForm,contractStart:e.target.value})}/>
            </div>
            <div>
              <label className="label">Contract End</label>
              <input type="date" className="input" value={payerForm.contractEnd} onChange={e=>setPayerForm({...payerForm,contractEnd:e.target.value})}/>
            </div>
          </div>
          <div>
            <label className="label">{t('common.address','Address')}</label>
            <textarea className="input" rows={2} value={payerForm.address} onChange={e=>setPayerForm({...payerForm,address:e.target.value})}/>
          </div>
          <div>
            <label className="label">{t('common.notes','Notes')}</label>
            <textarea className="input" rows={2} value={payerForm.notes} onChange={e=>setPayerForm({...payerForm,notes:e.target.value})}/>
          </div>
          <div className="flex gap-3">
            <button onClick={savePayer} disabled={saving} className="btn-primary flex-1 py-2.5">{saving?'Saving...':'💾 Save Payer'}</button>
            <button onClick={()=>setShowPayerForm(false)} className="btn-secondary px-6">{t('common.cancel','Cancel')}</button>
          </div>
        </div>
      </SlidePanel>

      {/* ═══ TARIFFS MODAL ═══ */}
      <SlidePanel isOpen={!!showTariffs} onClose={()=>setShowTariffs(null)} title={`💰 Tariffs — ${showTariffs?.name||''}`} size="xl">
        {showTariffs&&(
          <div>
            {/* Add tariff form */}
            <div className="card p-3 mb-4 bg-slate-50">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Add Service Tariff</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Select value={tariffForm.serviceCategory} onChange={(e:any)=>setTariffForm({...tariffForm,serviceCategory:e.target.value})}>
                  {SERVICE_CATEGORIES.map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}
                </Select>
                <input className="input" placeholder="Service name *" value={tariffForm.serviceName} onChange={e=>setTariffForm({...tariffForm,serviceName:e.target.value})}/>
                <input className="input" placeholder="Service code" value={tariffForm.serviceCode} onChange={e=>setTariffForm({...tariffForm,serviceCode:e.target.value})}/>
                <input type="number" className="input" placeholder="Standard price" value={tariffForm.standardPrice} onChange={e=>setTariffForm({...tariffForm,standardPrice:e.target.value})}/>
                <input type="number" className="input" placeholder="Negotiated price *" value={tariffForm.negotiatedPrice} onChange={e=>setTariffForm({...tariffForm,negotiatedPrice:e.target.value})}/>
              </div>
              <button onClick={addTariff} disabled={saving} className="btn-primary text-sm mt-2">+ Add Tariff</button>
            </div>

            {/* Tariff list grouped by category */}
            {tariffs.length===0?(
              <p className="text-slate-400 text-center py-8">No tariffs configured for this payer</p>
            ):(
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {SERVICE_CATEGORIES.filter(cat=>tariffs.some(t=>t.serviceCategory===cat)).map(cat=>(
                  <div key={cat}>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1.5">{cat.replace('_',' ')}</p>
                    <div className="space-y-1">
                      {tariffs.filter(t=>t.serviceCategory===cat).map((t:any,i:number)=>(
                        <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-sm font-medium text-slate-700">{t.serviceName}</p>
                            {t.serviceCode&&<p className="text-xs text-slate-400">{t.serviceCode}</p>}
                          </div>
                          <div className="flex items-center gap-3">
                            {t.standardPrice&&<span className="text-xs text-slate-400 line-through">{fmtCurrency(t.standardPrice)}</span>}
                            <span className="font-bold text-green-600">{fmtCurrency(t.negotiatedPrice)}</span>
                            <button onClick={()=>removeTariff(t.id)} className="text-red-400 hover:text-red-600">✕</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </SlidePanel>

      {/* ═══ NEW PRE-AUTH MODAL ═══ */}
      <SlidePanel isOpen={showAuthForm} onClose={()=>setShowAuthForm(false)} title="📋 New Pre-Authorization Request" size="lg">
        <div className="space-y-3">
          <PatientSearchInput label="Patient *" selected={selectedPatient} onSelect={setSelectedPatient} onClear={()=>setSelectedPatient(null)}/>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Payer *</label>
              <Select value={authForm.payerId} onChange={(e:any)=>setAuthForm({...authForm,payerId:e.target.value})}>
                <option value="">— Select Payer —</option>
                {payers.filter(p=>p.payerType!=='CASH').map((p:any)=><option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </div>
            <div>
              <label className="label">Relationship to Patient</label>
              <Select value={authForm.relationshipToPatient} onChange={(e:any)=>setAuthForm({...authForm,relationshipToPatient:e.target.value})}>
                {['Self','Spouse','Parent','Child','Other'].map(r=><option key={r}>{r}</option>)}
              </Select>
            </div>
            <div>
              <label className="label">Policy Number</label>
              <input className="input" value={authForm.policyNumber} onChange={e=>setAuthForm({...authForm,policyNumber:e.target.value})}/>
            </div>
            <div>
              <label className="label">Policy Holder Name</label>
              <input className="input" value={authForm.policyHolderName} onChange={e=>setAuthForm({...authForm,policyHolderName:e.target.value})}/>
            </div>
            <div>
              <label className="label">Requested Amount ({getCurrencySymbol()})</label>
              <input type="number" className="input" value={authForm.requestedAmount} onChange={e=>setAuthForm({...authForm,requestedAmount:e.target.value})}/>
            </div>
            <div>
              <label className="label">Expiry Date</label>
              <input type="date" className="input" value={authForm.expiryDate} onChange={e=>setAuthForm({...authForm,expiryDate:e.target.value})}/>
            </div>
          </div>
          <div>
            <label className="label">Diagnosis *</label>
            <textarea className="input" rows={2} value={authForm.diagnosis} onChange={e=>setAuthForm({...authForm,diagnosis:e.target.value})}/>
          </div>
          <div>
            <label className="label">Treatment Plan</label>
            <textarea className="input" rows={3} value={authForm.treatmentPlan} onChange={e=>setAuthForm({...authForm,treatmentPlan:e.target.value})}/>
          </div>
          <div className="flex gap-3">
            <button onClick={saveAuth} disabled={saving} className="btn-primary flex-1 py-2.5">{saving?'Submitting...':'📋 Submit Request'}</button>
            <button onClick={()=>setShowAuthForm(false)} className="btn-secondary px-6">{t('common.cancel','Cancel')}</button>
          </div>
        </div>
      </SlidePanel>

      {/* ═══ UPDATE AUTH STATUS MODAL ═══ */}
      <SlidePanel isOpen={!!showAuthDetail} onClose={()=>setShowAuthDetail(null)} title={`Update Authorization — ${showAuthDetail?.authNumber||showAuthDetail?.auth_number||''}`} size="sm">
        {showAuthDetail&&(
          <div className="space-y-3">
            <div>
              <label className="label">{t('common.status','Status')}</label>
              <Select value={authUpdate.status} onChange={(e:any)=>setAuthUpdate({...authUpdate,status:e.target.value})}>
                {Object.keys(AUTH_STATUS_META).map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
              </Select>
            </div>
            <div>
              <label className="label">Sanctioned Amount ({getCurrencySymbol()})</label>
              <input type="number" className="input" value={authUpdate.sanctionedAmount} onChange={e=>setAuthUpdate({...authUpdate,sanctionedAmount:e.target.value})}/>
            </div>
            <div>
              <label className="label">TPA Reference Number</label>
              <input className="input" value={authUpdate.tpaReferenceNumber} onChange={e=>setAuthUpdate({...authUpdate,tpaReferenceNumber:e.target.value})}/>
            </div>
            <div>
              <label className="label">{t('common.remarks','Remarks')}</label>
              <textarea className="input" rows={2} value={authUpdate.remarks} onChange={e=>setAuthUpdate({...authUpdate,remarks:e.target.value})}/>
            </div>
            <div className="flex gap-3">
              <button onClick={updateAuthStatus} disabled={saving} className="btn-primary flex-1 py-2.5">{saving?'Updating...':'Update'}</button>
              <button onClick={()=>setShowAuthDetail(null)} className="btn-secondary px-6">{t('common.cancel','Cancel')}</button>
            </div>
          </div>
        )}
      </SlidePanel>
    </Layout>
  );
}
